/** Shared API origin + dev key for Apple TV web surfaces. */
export const DEFAULT_DEV_KEY = process.env.REACT_APP_DEV_DEFAULT_API_KEY || "curator-dev-key";

export const PRODUCTION_APPLE_API = "https://www.curatorwatchlist.com";

export function selectAppleApiOrigin({
  configuredOrigin = "",
  runtimeHostname = "",
  runtimeOrigin = "",
} = {}) {
  const isLocalRuntime = /^(localhost|127(?:\.\d{1,3}){3}|\[?::1\]?)$/i.test(runtimeHostname);
  if (runtimeOrigin && !isLocalRuntime) return runtimeOrigin;
  if (configuredOrigin) return configuredOrigin;
  if (isLocalRuntime) return "http://localhost:3001";
  return runtimeOrigin || PRODUCTION_APPLE_API;
}

/** Production browsers use same-origin Vercel APIs; local development may use its API server. */
export const APPLE_API_ORIGIN = selectAppleApiOrigin({
  configuredOrigin: process.env.REACT_APP_API_URL,
  runtimeHostname: typeof window !== "undefined" ? window.location.hostname : "",
  runtimeOrigin: typeof window !== "undefined" ? window.location.origin : "",
});

export const REQUEST_HEADERS = {
  "x-api-key": DEFAULT_DEV_KEY,
  Accept: "application/json",
};

export async function fetchAppleEditorial(
  mediaKind,
  itemId,
  { request = fetchAppleApi, now = Date.now } = {},
) {
  if ((mediaKind !== "movie" && mediaKind !== "tv") || !itemId) return null;
  const response = await request(
    `/api/3/${mediaKind}/${itemId}/editorial?_ts=${now()}`,
    {
      headers: REQUEST_HEADERS,
      cache: "no-store",
    },
    {
      preferSameOrigin: true,
      requireJson: true,
    },
  );
  if (!response.ok) return null;
  return response.json();
}

export function resolveAppleApiOrigins({
  preferSameOrigin = false,
  currentOrigin = typeof window !== "undefined" ? window.location.origin : "",
} = {}) {
  const primary = APPLE_API_ORIGIN;
  const origins = preferSameOrigin && currentOrigin ? [currentOrigin, primary] : [primary];
  if (typeof window !== "undefined") {
    const isLocalPrimary = /localhost|127\.0\.0\.1/.test(primary);
    if (isLocalPrimary && !origins.includes(PRODUCTION_APPLE_API)) {
      origins.push(PRODUCTION_APPLE_API);
    }
    if (!isLocalPrimary && currentOrigin && !origins.includes(currentOrigin)) {
      origins.push(currentOrigin);
    }
  }
  return [...new Set(origins.filter(Boolean))];
}

export async function fetchAppleApi(
  path,
  options = {},
  { preferSameOrigin = false, requireJson = false } = {},
) {
  const origins = resolveAppleApiOrigins({ preferSameOrigin });
  let lastResponse = null;
  let lastError = null;

  for (const origin of origins) {
    try {
      const response = await fetch(`${origin}${path}`, options);
      if (response.ok) {
        const contentType = String(response.headers.get("content-type") || "").toLowerCase();
        if (!requireJson || contentType.includes("application/json")) return response;
        lastError = new Error(`API returned non-JSON from ${origin}.`);
        continue;
      }
      lastResponse = response;
      if (response.status === 404 && origins.length > 1) continue;
    } catch (err) {
      lastError = err;
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError || new Error("Could not reach Curator API.");
}

/** Parse JSON or throw if SPA HTML was returned (missing API route). */
export async function fetchJsonOrThrow(response, label = "request") {
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  const isJson = contentType.includes("application/json");
  if (!response.ok) {
    if (isJson) {
      const json = await response.json().catch(() => ({}));
      throw new Error(json?.status_message || json?.message || `${label} failed (HTTP ${response.status})`);
    }
    throw new Error(
      `${label} failed (HTTP ${response.status}). Restart the API server if this route was added recently.`,
    );
  }
  if (!isJson) {
    throw new Error(`${label} returned non-JSON — restart or deploy the latest API server.`);
  }
  return response.json();
}
